




























































































































































import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { History, SearchHistorysQueryVariables, SearchableHistoryFilterInput, SearchableHistorySortInput ,SearchableHistorySortableFields, SearchableSortDirection, SearchHistorysQuery, PGCService } from "@/API";
import { API, graphqlOperation } from "aws-amplify";
import * as queries from "@/graphql/queries";
import moment from "moment";
import * as AuthUtils from "@/utils/AuthUtils";
import * as RoleUtils from "@/utils/RoleUtils";
import { GraphQLResult } from "@aws-amplify/api";
import { getSetting } from "@/utils/AccountSettingUtils";

export interface HistoryData {
    id: string;
    operation: string;
    createdAt: string;
    userName: string;
    message: string;
}

interface TableHeader {
    text: string;
    value: string;
    sortable?: boolean;
}

interface OperationType {
    text: string;
    value: string;
}

interface CognitoUser {
    attributes?: {
        "custom:account_id"?: string;
        email?: string;
        sub?: string;
    };
}

// 定数定義
const DEFAULT_ITEMS_PER_PAGE = 30;
const ITEMS_PER_PAGE_OPTIONS = [10, 30, 50, 100];
const SNACKBAR_TIMEOUT_MS = 5000;

@Component
export default class HistoryMain extends Vue {
    // テンプレートから参照する定数（publicでないとテンプレートから参照できない）
    readonly itemsPerPageOptions = ITEMS_PER_PAGE_OPTIONS;
    readonly snackbarTimeout = SNACKBAR_TIMEOUT_MS;

    private headers: TableHeader[] = [
        { text: "操作", value: "operation" },
        { text: "操作日時", value: "createdAt" },
        { text: "操作ユーザー", value: "userName" },
        { text: "メッセージ", value: "message", sortable: false },
    ];
    private operationTypes: OperationType[] = [
        { text: "すべて", value: "" },
        { text: "ダウンロード", value: "download" },
    ];
    private page = 1;
    private itemsPerPage = DEFAULT_ITEMS_PER_PAGE;
    private tableLoading = false;
    private totalItems = 0;
    private dateFrom = "";
    private dateTo = "";
    private selectedOperation = "";
    private dateFromMenu = false;
    private dateToMenu = false;

    private items: HistoryData[] = [];
    private user: CognitoUser | null = null;
    private accountId = "";

    // レースコンディション対策用リクエストID
    private searchRequestId = 0;

    // エラー通知用
    private errorSnackbar = false;
    private errorMessage = "";

    // ユーザーキャッシュ
    private userCache: Map<string, string> | null = null; // Map<userId, userName>
    private userCacheFetchPromise: Promise<void> | null = null;
    // 進行中のユーザー名取得リクエスト（重複リクエスト防止用）
    private pendingUserRequests: Map<string, Promise<string>> = new Map();

    // 二重実行防止
    private isSearchTriggeredProgrammatically = false;

    // コンポーネント破棄フラグ
    private isDestroyed = false;

    @Prop({ default: false, type: Boolean })
    private show!: boolean;

    @Watch("show")
    private async onChangeShow(newValue: boolean): Promise<void> {
        if(newValue){
            // ローディング中は検索をスキップ
            if (this.tableLoading) {
                return;
            }
            this.isSearchTriggeredProgrammatically = true;
            this.page = 1;
            await this.search();
        }
    }

    @Watch("page")
    private async onPageChange(): Promise<void> {
        // プログラム的にトリガーされた場合はスキップ（手動でsearchを呼ぶため）
        if (this.isSearchTriggeredProgrammatically) {
            this.isSearchTriggeredProgrammatically = false;
            return;
        }
        // ローディング中は検索をスキップ
        if (this.tableLoading) {
            return;
        }
        await this.search();
    }

    @Watch("itemsPerPage")
    private async onItemsPerPageChange(): Promise<void> {
        // ローディング中は検索をスキップ
        if (this.tableLoading) {
            return;
        }
        this.isSearchTriggeredProgrammatically = true;
        this.page = 1;
        await this.search();
    }

    private async onSearchButtonClick(): Promise<void> {
        this.isSearchTriggeredProgrammatically = true;
        this.page = 1;
        await this.search();
    }

    private async onClearButtonClick(): Promise<void> {
        this.errorSnackbar = false;
        this.selectedOperation = "";
        this.dateFrom = "";
        this.dateTo = "";
        this.isSearchTriggeredProgrammatically = true;
        this.page = 1;
        this.userCache = null;

        try {
            await this.initializeUserCache();
            await this.search();
        } catch (error) {
            console.error("Clear and search failed:", error);
            this.tableLoading = false;
            this.isSearchTriggeredProgrammatically = false;
            this.errorMessage = 'クリア処理に失敗しました。';
            this.errorSnackbar = true;
        }
    }

    private async created(): Promise<void> {
        try {
            this.user = this.$store.getters.user;
            this.accountId = this.user?.attributes?.["custom:account_id"] ?? "";

            if (!this.user || !this.accountId) {
                this.$router.push({ name: "Home" });
                return;
            }

            if (!RoleUtils.isAllow("management", "history")) {
                this.$router.push({ name: "Home" });
                return;
            }

            try {
                const accountSetting = await getSetting();
                if(accountSetting){
                    if(accountSetting.sale){
                        this.operationTypes.push(
                            { text: "外販送信", value: "sale send" }
                        );
                    }
                    if(accountSetting.sharing){
                        this.operationTypes.push(
                            { text: "共有", value: "share" }
                        );
                    }
                }
            } catch (error) {
                console.error("Failed to get account settings:", error);
                this.errorMessage = 'アカウント設定の取得に失敗しました。';
                this.errorSnackbar = true;
            }

            try {
                await this.initializeUserCache();
            } catch (error) {
                console.error("Failed to initialize user cache:", error);
                this.errorMessage = 'ユーザー情報の取得に失敗しました。ユーザー名が正しく表示されない場合があります。';
                this.errorSnackbar = true;
            }
        } catch (error) {
            console.error("Component initialization failed:", error);
            this.$router.push({ name: "Home" });
        }
    }

    /**
     * ユーザーキャッシュをCognitoから全ユーザーで初期化
     * 並行フェッチを防ぐためPromiseキャッシュを使用
     */
    private async initializeUserCache(): Promise<void> {
        // 既にロード済みの場合は既存キャッシュを返す
        if (this.userCache !== null) {
            return;
        }

        // 既にフェッチ中の場合はそのPromiseを待つ
        if (this.userCacheFetchPromise !== null) {
            return this.userCacheFetchPromise;
        }

        // 新しいフェッチを開始
        this.userCacheFetchPromise = (async () => {
            try {
                const allUsers: Array<{ key: string | undefined; val: string | undefined }> =
                    await AuthUtils.listUsers(PGCService.MANAGEMENT, "");

                // Map構築: userId -> userName
                this.userCache = new Map<string, string>();
                for (const user of allUsers) {
                    if (user.val && user.key) {
                        this.userCache.set(user.val, user.key);
                    }
                }

            } catch (error) {
                console.error("Failed to initialize user cache:", error);
                // エラー時は空のキャッシュを設定（再試行ループを防ぐ）
                this.userCache = new Map<string, string>();
            } finally {
                this.userCacheFetchPromise = null;
            }
        })();

        return this.userCacheFetchPromise;
    }

    /**
     * キャッシュからユーザー名を取得（フォールバック付き）
     * 同一userIdへの重複リクエストを防止
     */
    private async getUserNameFromCache(userId: string): Promise<string> {
        if (!this.userCache) {
            return "";
        }

        // キャッシュにあればそれを返す
        const cachedName = this.userCache.get(userId);
        if (cachedName !== undefined) {
            return cachedName;
        }

        // 既に同じuserIdのリクエストが進行中なら、そのPromiseを待つ
        const pendingRequest = this.pendingUserRequests.get(userId);
        if (pendingRequest) {
            return pendingRequest;
        }

        // 新規リクエストを開始し、進行中マップに登録
        const fetchPromise = (async () => {
            try {
                let userName = await AuthUtils.getUserName(userId);
                userName = userName || "無効・削除済み";
                if (this.userCache) {
                    this.userCache.set(userId, userName);
                }
                return userName;
            } finally {
                this.pendingUserRequests.delete(userId);
            }
        })();

        this.pendingUserRequests.set(userId, fetchPromise);
        return fetchPromise;
    }

    private async search(): Promise<void> {
        const currentRequestId = ++this.searchRequestId;
        this.tableLoading = true;

        if (this.isDestroyed) {
            this.tableLoading = false;
            return;
        }

        if(!this.accountId){
            this.tableLoading = false;
            return;
        }

        const filter: SearchableHistoryFilterInput = {
            accountId: {
                eq: this.accountId,
            },
        }

        if(this.selectedOperation.trim()) {
            filter.operation = {
                eq: this.selectedOperation.trim(),
            };
        }

        if(this.dateFrom || this.dateTo) {
            // 日付範囲のバリデーション
            if(this.dateFrom && this.dateTo) {
                const from = moment(this.dateFrom);
                const to = moment(this.dateTo);

                if(from.isAfter(to)) {
                    this.errorMessage = '開始日は終了日より前の日付を指定してください。';
                    this.errorSnackbar = true;
                    this.tableLoading = false;
                    return;
                }
            }

            filter.createdAt = {};

            if(this.dateFrom) {
                const fromDate = moment(this.dateFrom).startOf('day').utc().format();
                filter.createdAt.gte = fromDate;
            }

            if(this.dateTo) {
                const toDate = moment(this.dateTo).endOf('day').utc().format();
                filter.createdAt.lte = toDate;
            }
        }

        const sort: SearchableHistorySortInput = {
            field: SearchableHistorySortableFields.createdAt,
            direction: SearchableSortDirection.desc,
        }

        // 変数オブジェクトを構築
        const variables: SearchHistorysQueryVariables = {
            filter: filter,
            sort: sort,
            limit: this.itemsPerPage,
        };

        // ページネーション: fromベースで計算
        variables.from = (this.page - 1) * this.itemsPerPage;

        try{
            const { data } = (await API.graphql(graphqlOperation(queries.searchHistorys, variables))) as GraphQLResult<SearchHistorysQuery>;

            // レースコンディション対策: 古いリクエストの結果は破棄
            if (currentRequestId !== this.searchRequestId) {
                return;
            }

            // コンポーネント破棄後の状態更新を防止
            if (this.isDestroyed) {
                return;
            }

            if(data && data.searchHistorys){
                const histories = (data.searchHistorys.items ?? []).filter((item): item is History => item !== null);
                const total = data.searchHistorys.total || 0;
                this.totalItems = total;

                // キャッシュされたユーザーデータを使用して履歴を処理
                const historyDataPromises = histories.map(async (history) => {
                    const userName = await this.getUserNameFromCache(history.userId || "");
                    const historyData: HistoryData = {
                        id: history.id || "",
                        operation: history.operation ? this.getOperation(history.operation) : "",
                        createdAt: history.createdAt ? moment(history.createdAt).local().format("YYYY-MM-DD HH:mm:ss") : "",
                        userName: userName,
                        message: history.body || "",
                    };
                    return historyData;
                });

                const historyDataResults = await Promise.all(historyDataPromises);
                this.items = historyDataResults.filter((item): item is HistoryData => item !== null);
            }
        }catch(error){
            console.error("Search error:", error);
            this.errorMessage = '検索に失敗しました。時間をおいて再度お試しください。';
            this.errorSnackbar = true;
        }finally{
            this.tableLoading = false;
            this.isSearchTriggeredProgrammatically = false;
        }
    }

    private getOperation(operation: string): string {
        const operationMap: Record<string, string> = {
            "share": "共有",
            "download": "ダウンロード",
            "download request": "ダウンロード要求",
            "sale send": "外販送信",
        };
        // 未知の操作タイプは原文を表示（フォールバック）
        return operationMap[operation] ?? operation;
    }

    private beforeDestroy(): void {
        // コンポーネント破棄フラグを設定
        this.isDestroyed = true;
        // クリーンアップ: ユーザーキャッシュをクリア
        this.userCache = null;
        this.userCacheFetchPromise = null;
        this.pendingUserRequests.clear();
    }
}
